import { type ReactElement } from 'react';
import { Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  DataTableHeaderRow,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableTr,
} from '@/components/ui/data-table';

import type { TenantRoleViewModel } from '../api';
import { roleDisplayLabel } from '../roleLabel';

/** Tenant role-catalogue table (PTV2-1444): rows open the role record; label shown, not the raw name. */
export function TenantRolesTable({
  roles,
  emptyCell,
  onOpen,
  onDelete,
}: {
  readonly roles: readonly TenantRoleViewModel[];
  readonly emptyCell: string;
  readonly onOpen: (role: TenantRoleViewModel) => void;
  readonly onDelete: (role: TenantRoleViewModel) => void;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <DataTableTable aria-label={t('routes.platform.detail.roles.table.ariaLabel')}>
      <DataTableThead className="sticky top-0 z-10 bg-card">
        <DataTableHeaderRow>
          <DataTableTh widthClassName="w-[35%] min-w-[10rem]">
            {t('routes.platform.detail.roles.table.name')}
          </DataTableTh>
          <DataTableTh widthClassName="w-[55%] min-w-[12rem]">
            {t('routes.platform.detail.roles.table.description')}
          </DataTableTh>
          <DataTableTh widthClassName="w-[10%]" align="right">
            <span className="sr-only">{t('routes.platform.detail.roles.table.actions')}</span>
          </DataTableTh>
        </DataTableHeaderRow>
      </DataTableThead>
      <DataTableTbody>
        {roles.map((role) => (
          <DataTableTr key={role.id.length > 0 ? role.id : role.name}>
            <DataTableTd className="font-medium text-foreground" dataOffset={false}>
              <button
                type="button"
                className="text-left hover:underline"
                onClick={() => { onOpen(role); }}
              >
                {roleDisplayLabel(role.name)}
              </button>
            </DataTableTd>
            <DataTableTd className="whitespace-normal break-words text-foreground/90" dataOffset>
              {role.description.length > 0 ? role.description : emptyCell}
            </DataTableTd>
            <DataTableTd align="right" dataOffset>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-destructive disabled:opacity-40"
                onClick={() => { onDelete(role); }}
                disabled={role.builtIn}
                title={role.builtIn ? t('routes.platform.detail.roles.builtInLocked') : undefined}
                aria-label={t('routes.platform.detail.roles.deleteAction', { name: roleDisplayLabel(role.name) })}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </Button>
            </DataTableTd>
          </DataTableTr>
        ))}
      </DataTableTbody>
    </DataTableTable>
  );
}
